import styles from './submission-detail-panel.module.css'

import { LuChevronLeft } from '@react-icons/all-files/lu/LuChevronLeft'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuDownload } from '@react-icons/all-files/lu/LuDownload'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import React from 'react'

import { PopoverWithClose } from '@disco/shared-components/ui/popover-with-close'
import { SlideoutPanel } from '@disco/shared-components/ui/slideout-panel'
import {
  type LicenseRequestSubmission,
  type LicenseRequestSubmissionTrack,
  useGetRequestFormVersionQuery,
} from '@disco/state-management/api/license-requests'
import { getInitials } from '@disco/utils/string-utils'

import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Separator,
  Spinner,
  Text,
} from 'components/ds'

import { formatDate } from '../format-date'

/** Fields rendered as static entries or handled by the track list */
const SKIP_FIELDS = new Set(['user_name', 'user_email', 'tracks', 'playlist'])

type FieldRowProps = {
  label: string
  value: React.ReactNode
}

const FieldRow = ({ label, value }: FieldRowProps) => (
  <Flex direction="column" gap="1">
    <Text size="2" weight="medium">
      {label}
    </Text>
    <Box className={styles.fieldValue}>
      <Text size="2">{value == null || value === '' ? '—' : value}</Text>
    </Box>
  </Flex>
)

type TrackInfoPopoverProps = {
  track: LicenseRequestSubmissionTrack & {
    isrc?: string
    release_id?: string | number
    disco_id?: string | number
  }
}

const TrackInfoPopover = ({ track }: TrackInfoPopoverProps) => {
  return (
    <PopoverWithClose
      content={
        <Flex direction="column" gap="2">
          {track.isrc && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                ISRC
              </Text>
              <Text size="2">{track.isrc}</Text>
            </Flex>
          )}
          {track.release_id && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                Release ID
              </Text>
              <Text size="2">{track.release_id}</Text>
            </Flex>
          )}
          {track.id && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                DISCO ID
              </Text>
              <Text size="2">{track.id}</Text>
            </Flex>
          )}
        </Flex>
      }
    >
      <IconButton size="1" variant="ghost" color="gray" aria-label="Track info">
        <LuInfo size={14} />
      </IconButton>
    </PopoverWithClose>
  )
}

type Props = {
  open: boolean
  submission: LicenseRequestSubmission | null
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  prevDisabled: boolean
  nextDisabled: boolean
}

export const SubmissionDetailPanel = ({
  open,
  submission,
  onClose,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: Props) => {
  const { data: formVersion, isLoading: formVersionLoading } =
    useGetRequestFormVersionQuery(submission?.form_version_id ?? 0, {
      skip: !submission,
    })

  if (!submission) return null

  const { answers, tracks } = submission
  const initials = getInitials(String(answers.user_name ?? ''))

  const playlistName = answers.playlist?.name

  const dynamicFields = (() => {
    if (!formVersion) return []
    const { order } = formVersion.definition.ui
    const { properties } = formVersion.definition.schema
    return order
      .filter(key => !SKIP_FIELDS.has(key))
      .filter(key => {
        const field = properties[key]
        if (!field) return false
        const discoType = field['x-disco-type']
        return (
          discoType !== 'track_reference' && discoType !== 'playlist_reference'
        )
      })
      .map(key => ({
        key,
        label: properties[key]?.title ?? key,
        value: answers[key] as string | number | null | undefined,
      }))
  })()

  return (
    <SlideoutPanel
      open={open}
      onClose={onClose}
      header={
        <Flex align="center" gap="2">
          <Avatar
            size="1"
            fallback={initials || '?'}
            radius="full"
            className={styles.avatar}
          />
          <Text size="3" weight="bold" className={styles.headerName}>
            {answers.user_name}
          </Text>
        </Flex>
      }
      zIndex={101}
      headerActions={
        <Flex gap="2" mr="1">
          <IconButton
            size="1"
            variant="soft"
            color="gray"
            disabled={prevDisabled}
            onClick={onPrev}
            aria-label="Previous"
          >
            <LuChevronLeft size={22} />
          </IconButton>
          <IconButton
            size="1"
            variant="soft"
            color="gray"
            disabled={nextDisabled}
            onClick={onNext}
            aria-label="Next"
          >
            <LuChevronRight size={22} />
          </IconButton>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled
            aria-label="Download"
          >
            <LuDownload size={22} />
          </IconButton>
        </Flex>
      }
    >
      <Flex direction="column" gap="3">
        <FieldRow
          label="Submission Date"
          value={formatDate(submission.created)}
        />
        <FieldRow label="Request ID" value={submission.id} />
        <FieldRow label="Name" value={String(answers.user_name ?? '')} />
        <FieldRow label="Email" value={String(answers.user_email ?? '')} />

        {formVersionLoading && (
          <Flex justify="center" py="4">
            <Spinner />
          </Flex>
        )}

        {dynamicFields.map(field => (
          <FieldRow
            key={field.key}
            label={field.label}
            value={field.value != null ? String(field.value) : undefined}
          />
        ))}

        {tracks.length > 0 && (
          <>
            <Separator size="4" />
            <Text size="2" weight="medium">
              <span>{playlistName && `${playlistName} | `}</span>
              {`Tracks (${tracks.length})`}
            </Text>
            <Flex direction="column" gap="3">
              {tracks.map(track => (
                <Flex key={track.id} align="center" gap="3">
                  {track.artwork?.artwork_sizes['160x160'] ? (
                    <img
                      src={track.artwork.artwork_sizes['160x160']}
                      alt={`Artwork for ${track.name}`}
                      className={styles.trackArtwork}
                    />
                  ) : (
                    <div className={styles.trackArtworkPlaceholder} />
                  )}
                  <Flex direction="column" flexGrow="1" minWidth="0">
                    <Text size="2" weight="medium" truncate>
                      {track.name}
                    </Text>
                    <Text size="1" color="gray" truncate>
                      {track.artist}
                    </Text>
                  </Flex>
                  <TrackInfoPopover track={track} />
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </SlideoutPanel>
  )
}
